import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  Mail,
  Phone,
  FileText,
  MessageSquare,
} from "lucide-react";
import { brandConfig } from "../../../config/brandConfig";
import logoImg from "../../../assets/images/cropped_logo.png";
import fabIcon from "../../../assets/icons/fab.png";
import { useCategories } from "../../../hooks/useCategories";
import styles from "./KristNavbar.module.css";

const mainNavLinks = [
  { label: "Home", href: "/" },
  { label: "Collections", href: "/collections", hasDropdown: true },
  { label: "Events", href: "/gallery" },
  { label: "Branches", href: "/branches" },
  { label: "About Us", href: "/about" },
  { label: "Contact", href: "/contact" },
];

const secondaryLinks = [
  { label: "T&C", href: "/terms" },
  { label: "Privacy", href: "/privacy" },
];

const BrandLogo = () => (
  <Link
    to="/"
    className={styles.logoLink}
    aria-label={`${brandConfig.brand_name} Home`}
  >
    <div
      className={styles.logoMarkImg}
      style={{ display: "flex", alignItems: "center", gap: "10px" }}
    >
      <img
        src={fabIcon}
        alt="Icon"
        style={{ height: "100%", width: "auto", objectFit: "contain" }}
      />
      <img
        src={logoImg}
        alt={brandConfig.brand_name}
        className={styles.logoImage}
        style={{ height: "75%" }}
      />
    </div>
  </Link>
);

const WhatsAppIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12 2C6.477 2 2 6.477 2 12c0 1.762.47 3.413 1.29 4.843L2 22l5.338-1.272A9.948 9.948 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18c-1.614 0-3.12-.423-4.418-1.162l-.318-.188-3.165.754.786-3.077-.207-.327A7.94 7.94 0 014 12c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
  </svg>
);

const InstagramIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const FacebookIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
  </svg>
);

const YoutubeIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33 2.78 2.78 0 0 0 1.94 2c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.33 29 29 0 0 0-.46-5.33z"></path>
    <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
  </svg>
);

const KristNavbar = () => {
  const navigate = useNavigate();
  const { categories } = useCategories();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleInternalNavLink = (href) => (e) => {
    e.preventDefault();
    setIsMenuOpen(false);
    navigate(href, { state: { navTimestamp: Date.now() } });
  };

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const phoneNumbers = brandConfig.phone_number.split(",").map((n) => n.trim());

  return (
    <>
      <header
        className={`${styles.navbar} ${isScrolled ? styles.scrolled : ""}`}
        role="banner"
      >
        {/* Top Strip */}
        <div className={styles.topStrip}>
          <div
            className={styles.container}
            style={{ padding: "0 24px", height: "100%", alignItems: "center" }}
          >
            <div className={styles.topStripInner}>
              {/* Left Side: Email */}
              <div className={styles.topStripLeft}>
                <a
                  href={`mailto:${brandConfig.email}`}
                  className={styles.topStripItem}
                >
                  <Mail size={14} /> {brandConfig.email}
                </a>
              </div>

              {/* Center: Social Icons */}
              <div className={styles.topStripCenter}>
                {brandConfig.instagram_url && (
                  <a
                    href={brandConfig.instagram_url}
                    className={styles.topStripItem}
                    style={{ color: "#E4405F" }}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                  >
                    <InstagramIcon size={14} />
                  </a>
                )}
                <span className={styles.topStripSeparator}></span>
                {brandConfig.facebook_url && (
                  <a
                    href={brandConfig.facebook_url}
                    className={styles.topStripItem}
                    style={{ color: "#1877F2" }}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                  >
                    <FacebookIcon size={14} />
                  </a>
                )}
                <span className={styles.topStripSeparator}></span>
                <a
                  href={brandConfig.whatsapp_chat_url}
                  className={styles.topStripItem}
                  style={{ color: "#25d366" }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <WhatsAppIcon size={16} />
                </a>
                <span className={styles.topStripSeparator}></span>
                <a
                  href={brandConfig.youtube_url || "https://youtube.com"}
                  className={styles.topStripItem}
                  style={{ color: "#FF0000" }}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                >
                  <YoutubeIcon size={14} />
                </a>
              </div>

              {/* Right Side: Phone Numbers */}
              <div className={styles.topStripRight}>
                {phoneNumbers.map((phone, index) => (
                  <React.Fragment key={phone}>
                    <a href={`tel:${phone}`} className={styles.topStripItem}>
                      <Phone size={14} /> {phone}
                    </a>
                    {index < phoneNumbers.length - 1 && (
                      <span className={styles.topStripSeparator}></span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.container}>
          {/* Logo */}
          <BrandLogo />

          {/* Desktop Navigation Group */}
          <div className={styles.navGroup}>
            <nav className={styles.desktopNav} aria-label="Main navigation">
              {mainNavLinks.map(({ label, href, hasDropdown }) => (
                <div
                  key={label}
                  className={hasDropdown ? styles.navItemDropdown : ""}
                >
                  <Link to={href} className={styles.navLink}>
                    {label}
                    {hasDropdown && (
                      <ChevronDown
                        size={14}
                        aria-hidden="true"
                        className={styles.dropdownIcon}
                      />
                    )}
                  </Link>
                  {hasDropdown && categories.length > 0 && (
                    <div className={styles.dropdownMenu}>
                      {categories.map((cat) => (
                        <a
                          key={cat.id}
                          href={`/collections?category=${cat.id}`}
                          className={styles.dropdownItem}
                          onClick={handleInternalNavLink(
                            `/collections?category=${cat.id}`,
                          )}
                        >
                          {cat.name}
                        </a>
                      ))}
                      <Link
                        to="/collections"
                        className={styles.dropdownItem}
                        style={{
                          fontWeight: "bold",
                          borderTop: "1px solid #eee",
                          marginTop: "5px",
                          paddingTop: "8px",
                        }}
                      >
                        View All Collections
                      </Link>
                    </div>
                  )}
                </div>
              ))}

              <span className={styles.navSeparator} aria-hidden="true" />

              {secondaryLinks.map(({ label, href }) => (
                <Link key={label} to={href} className={styles.navLink}>
                  {label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Actions */}
          <div className={styles.actions}>
            {/* Bulk Enquiry CTA */}
            <Link
              to="/enquiry"
              className={styles.enquiryBtn}
              aria-label="Start bulk enquiry"
            >
              <FileText size={16} />
              <span>Bulk Enquiry</span>
            </Link>

            <button
              className={styles.menuBtn}
              onClick={() => setIsMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Overlay */}
      {isMenuOpen && (
        <div
          className={styles.overlay}
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Drawer */}
      <nav
        className={`${styles.mobileDrawer} ${isMenuOpen ? styles.drawerOpen : ""}`}
        aria-label="Mobile navigation"
      >
        <div className={styles.drawerHeader}>
          <BrandLogo />
          <button
            className={styles.closeBtn}
            onClick={() => setIsMenuOpen(false)}
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        <ul className={styles.mobileLinks}>
          {mainNavLinks.map(({ label, href }) => (
            <li key={label}>
              <Link
                to={href}
                className={styles.mobileLink}
                onClick={() => setIsMenuOpen(false)}
              >
                {label}
              </Link>
            </li>
          ))}
          {/* T&C and Privacy as standard mobile links */}
          {secondaryLinks.map(({ label, href }) => (
            <li key={label}>
              <Link
                to={href}
                className={styles.mobileLink}
                onClick={() => setIsMenuOpen(false)}
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>

        <div className={styles.mobileActions}>
          <a
            href={brandConfig.whatsapp_chat_url}
            className={styles.mobileWhatsapp}
            target="_blank"
            rel="noopener noreferrer"
          >
            <WhatsAppIcon size={18} />
            Chat on WhatsApp
          </a>
          <Link to="/enquiry" className={styles.mobileEnquiry}>
            <MessageSquare size={18} />
            Start Bulk Enquiry
          </Link>
        </div>

        <div className={styles.drawerContact}>
          {phoneNumbers.map((phone) => (
            <a
              key={phone}
              href={`tel:${phone}`}
              style={{ marginBottom: "8px" }}
            >
              <Phone size={14} /> {phone}
            </a>
          ))}
        </div>
      </nav>
    </>
  );
};

export default KristNavbar;
